import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Users } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrgJoin() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!token) { setErr("No invite token"); setLoading(false); return; }
    axios.get(`${API}/org/invite/${token}`)
      .then((r) => setInvite(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Invite not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    if (!user) {
      navigate(`/login?invite=${token}&email=${encodeURIComponent(invite.email)}`);
      return;
    }
    setJoining(true);
    try {
      await axios.post(`${API}/org/join`, { token });
      await refresh();
      toast.success(`You joined ${invite.org_name}.`);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
    } finally {
      setJoining(false);
    }
  };

  if (loading) return <div className="container-wide py-24"><div className="h-4 w-48 bg-[#F5F5F5] animate-pulse" /></div>;

  if (err || !invite) {
    return (
      <div className="container-wide py-24 text-center">
        <p className="font-display text-3xl font-black">Invite unavailable</p>
        <p className="text-[#525252] mt-3">{err || "This invite may have been revoked or already used."}</p>
      </div>
    );
  }

  return (
    <div className="container-wide py-16 md:py-24">
      <div className="max-w-lg mx-auto border border-[#E5E7EB] bg-white p-8">
        <Users size={32} weight="duotone" className="text-[#002FA7]" />
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7]">Team Invite</p>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-black tracking-tighter">
          Join {invite.org_name} on Ordeneli
        </h1>
        <p className="mt-3 text-[#525252]">
          You were invited as <strong className="text-[#0A0A0A]">{invite.role}</strong> to <strong className="text-[#0A0A0A]">{invite.email}</strong> on the{" "}
          <span className="capitalize font-semibold">{invite.plan}</span> plan.
        </p>
        {user && user.email.toLowerCase() !== invite.email.toLowerCase() && (
          <p className="mt-4 text-sm text-[#DC2626] border border-[#FECACA] bg-[#FEF2F2] p-3">
            You're signed in as <strong>{user.email}</strong>, but this invite is for <strong>{invite.email}</strong>.
            Sign out and sign back in with the invited email.
          </p>
        )}
        <button
          onClick={accept}
          disabled={joining || (user && user.email.toLowerCase() !== invite.email.toLowerCase())}
          className="btn-primary mt-6 justify-center w-full"
          data-testid="accept-invite-btn"
        >
          {joining ? "Joining…" : user ? <>Accept invite <CheckCircle size={16} weight="bold" /></> : "Sign in to accept"}
        </button>
      </div>
    </div>
  );
}
